import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { keyFromName, type TaskArea, useTaskAreas } from "./taskData";

// The whole team manages the areas (D-020, D-028): add, rename, reorder,
// turn off. There is no delete: an area in use stays, turned off.
export const TaskAreasDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data: areas = [] } = useTaskAreas();
  const [newName, setNewName] = useState("");

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    await queryClient.invalidateQueries({ queryKey: ["task_areas"] });
  };

  const update = (area: TaskArea, data: Partial<TaskArea>) =>
    dataProvider.update("task_areas", {
      id: area.id,
      data,
      previousData: area,
    });

  const rename = (area: TaskArea, name: string) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === area.name) return;
    return run(() => update(area, { name: trimmed }));
  };

  const move = (index: number, offset: -1 | 1) => {
    const area = areas[index];
    const other = areas[index + offset];
    if (!other) return;
    return run(async () => {
      await update(area, { position: other.position });
      await update(other, { position: area.position });
    });
  };

  const add = (event: React.FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setNewName("");
    const position = Math.max(0, ...areas.map((area) => area.position)) + 1;
    return run(() =>
      dataProvider.create("task_areas", {
        data: { key: keyFromName(name), name, position, active: true },
      }),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{translate("tenax.tasks.areas.title")}</DialogTitle>
          <DialogDescription>
            {translate("tenax.tasks.areas.hint")}
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-2">
          {areas.map((area, index) => (
            <li key={area.id} className="flex items-center gap-1">
              <Input
                key={`${area.id}-${area.name}`}
                defaultValue={area.name}
                aria-label={translate("tenax.tasks.areas.name")}
                className={
                  area.active ? "" : "text-muted-foreground line-through"
                }
                onBlur={(event) => rename(area, event.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={index === 0}
                onClick={() => move(index, -1)}
                aria-label={translate("tenax.tasks.areas.move_up")}
              >
                <ArrowUp />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={index === areas.length - 1}
                onClick={() => move(index, 1)}
                aria-label={translate("tenax.tasks.areas.move_down")}
              >
                <ArrowDown />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-24"
                onClick={() =>
                  run(() => update(area, { active: !area.active }))
                }
              >
                {translate(
                  area.active
                    ? "tenax.tasks.areas.turn_off"
                    : "tenax.tasks.areas.turn_on",
                )}
              </Button>
            </li>
          ))}
        </ul>

        <form onSubmit={add} className="flex gap-2">
          <Input
            value={newName}
            placeholder={translate("tenax.tasks.areas.name")}
            aria-label={translate("tenax.tasks.areas.name")}
            onChange={(event) => setNewName(event.target.value)}
          />
          <Button type="submit" disabled={!newName.trim()}>
            {translate("tenax.tasks.areas.add")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
